import React, { useState, useEffect, useContext } from 'react';
import { noop } from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import {
    MachineTranslationButton as MTButton,
    raiseErrorToast,
    setTranslateOnLoad,
    getTranslateOnLoad,
} from '@fiverr-private/machine_translation';
import { Container } from '@fiverr-private/layout_components';
import { logger } from '@fiverr-private/obs';
import { translateUgcContent } from '../../shared/utils/personalizedContent';
import { buildSourceInfo } from '../../shared/utils/machineTranslation/buildSourceInfo';
import SafeComponent from '../../shared/SafeComponent';
import { UGCContext } from '../../context/ugc/ugc.context';
import { useUser } from '../../context/user/userProvider';
import { MachineTranslationButtonProps } from './types';

const MachineTranslationButton: React.FC<MachineTranslationButtonProps> = ({
    onToggle = noop,
    useStickyTranslation = false,
}) => {
    const { userData } = useUser();
    const { locale } = getContext();
    const { ugcMap, showTranslatedContent } = useContext(UGCContext);
    const { LEAN_WITH_DISCLAIMER } = MTButton.STYLES;

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            await stickyTranslate();
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sourceInfo = buildSourceInfo({ user: userData });

    const onClick = async () => {
        if (useStickyTranslation) {
            setTranslateOnLoad(!showTranslatedContent);
        }

        if (showTranslatedContent || !!ugcMap.translated) {
            onToggle({});
        } else {
            await translateContent();
        }
    };

    const translateContent = async () => {
        try {
            setLoading(true);
            const translatedPersonalizedContent = await translateUgcContent({
                ugcMap,
                targetLocale: locale,
                sourceInfo,
            });

            onToggle({ translatedPersonalizedContent });

            setLoading(false);
        } catch (error: any) {
            setLoading(false);
            raiseErrorToast();

            logger.error(error, { description: 'Error translation content' });
        }
    };

    const isTranslationOnLoadEnabled = (): boolean => useStickyTranslation && getTranslateOnLoad();

    const stickyTranslate = async () => {
        if (isTranslationOnLoadEnabled()) {
            await translateContent();
        }
    };

    return (
        <Container
            paddingRight={{ default: '0', sm: '6' }}
            paddingBottom="6"
            data-testid="machine-translation-button-wrapper"
        >
            <MTButton
                style={LEAN_WITH_DISCLAIMER}
                loading={loading}
                toggled={showTranslatedContent}
                sourceInfo={sourceInfo}
                onClick={onClick}
            />
        </Container>
    );
};

export { MachineTranslationButton };
export default SafeComponent(MachineTranslationButton);
